import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { loadScheduleItems, loadSectionTexts } from "../../utils/contentService";

import LocationModal from "../common/LocationModal";

export default function ScheduleSection() {
  const { i18n } = useTranslation();
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [texts, setTexts] = useState({});
  const [infoTexts, setInfoTexts] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDetails, setModalDetails] = useState("");

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const items = await loadScheduleItems(i18n.language);
        const validItems = items.filter(item =>
          item.title &&
          item.title !== "..." &&
          item.time &&
          item.time !== "..."
        );

        // Map to the format expected by the UI (with alternating sides)
        const mapped = validItems.map((item, index) => ({
          time: item.time,
          title: item.title,
          locationLink: item.locationLink || "",
          locationDetails: item.locationDetails || "",
          locationMapUrl: item.locationMapUrl || "",
          side: index % 2 === 0 ? "right" : "left",
        }));
        setScheduleEvents(mapped);
      } catch (error) {
        console.error("Failed to load schedule items:", error);
      }
    };

    fetchSchedule();
    setTexts(loadSectionTexts("schedule", i18n.language));
    setInfoTexts(loadSectionTexts("info", i18n.language));
  }, [i18n.language]);

  const [modalMapUrl, setModalMapUrl] = useState("");

  const handleLocationClick = (item) => {
    setModalTitle(item.locationLink);
    setModalDetails(item.locationDetails);
    setModalMapUrl(item.locationMapUrl);
    setIsModalOpen(true);
  };

  return (
    <section className="w-full py-12 md:py-16 lg:py-20 bg-test overflow-hidden">
      <LocationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        details={modalDetails}
        mapUrl={modalMapUrl}
        mapButtonText={infoTexts.mapButtonSchedule || "Google Maps"}
        closeText={infoTexts.close}
      />

      <div className="mx-auto max-w-[1100px] px-4">
        <h2 className="text-[24px] min-[350px]:text-[30px] lg:text-[45px] leading-tight lg:leading-[50px] italic font-medium font-serif text-center mb-6 min-[350px]:mb-8 lg:mb-16 text-black">
          {texts.title || ""}
        </h2>

        {/* Timeline Container - Flex Column (Fully Relative) */}
        <div className="flex flex-col items-center w-full">
          {/* Top Extension Line */}
          <div className="w-0.5 bg-black h-[12px] min-[350px]:h-[15px] lg:h-[20px] flex-none"></div>

          {scheduleEvents.map((item, index) => (
            <div key={index} className="flex w-full items-stretch">
              {/* LEFT SIDE */}
              <div className="flex-1 flex items-center justify-end min-h-[60px] min-[350px]:min-h-[80px] lg:min-h-[120px]">
                {item.side === "left" && (
                  <div className="flex items-center">
                    <div className="text-right mr-1.5 min-[350px]:mr-3 lg:mr-8">
                      <p className="text-[14px] min-[350px]:text-[18px] lg:text-[30px] font-medium font-serif italic text-black leading-tight lg:leading-[35px] mb-0.5 lg:mb-1">
                        {item.time}
                      </p>
                      <p className="text-[14px] min-[350px]:text-[18px] lg:text-[30px] font-medium font-serif italic text-black leading-tight lg:leading-[35px]">
                        {item.title}
                      </p>
                      {item.locationLink && (
                        <p
                          onClick={() => handleLocationClick(item)}
                          className="text-[14px] min-[350px]:text-[18px] lg:text-[30px] font-medium font-serif italic text-black leading-tight mt-0.5 lg:mt-1 underline cursor-pointer hover:opacity-60 transition-opacity"
                        >
                          {item.locationLink}
                        </p>
                      )}
                    </div>
                    <div className="w-[35px] min-[350px]:w-[60px] lg:w-[100px] h-0.5 bg-black flex-shrink-0"></div>
                  </div>
                )}
              </div>

              {/* CENTER VERTICAL LINE SEGMENT — stretches to full row height */}
              <div className="w-0.5 bg-black flex-none self-stretch"></div>

              {/* RIGHT SIDE */}
              <div className="flex-1 flex items-center justify-start min-h-[60px] min-[350px]:min-h-[80px] lg:min-h-[120px]">
                {item.side === "right" && (
                  <div className="flex items-center">
                    <div className="w-[35px] min-[350px]:w-[60px] lg:w-[100px] h-0.5 bg-black flex-shrink-0"></div>
                    <div className="text-left ml-1.5 min-[350px]:ml-3 lg:ml-8">
                      <p className="text-[14px] min-[350px]:text-[18px] lg:text-[30px] font-medium font-serif italic text-black leading-tight lg:leading-[35px] mb-0.5 lg:mb-1">
                        {item.time}
                      </p>
                      <p className="text-[14px] min-[350px]:text-[18px] lg:text-[30px] font-medium font-serif italic text-black leading-tight lg:leading-[35px]">
                        {item.title}
                      </p>
                      {item.locationLink && (
                        <p
                          onClick={() => handleLocationClick(item)}
                          className="text-[14px] min-[350px]:text-[18px] lg:text-[30px] font-medium font-serif italic text-black leading-tight mt-0.5 lg:mt-1 underline cursor-pointer hover:opacity-60 transition-opacity"
                        >
                          {item.locationLink}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Bottom Extension Line */}
          <div className="w-0.5 bg-black h-[12px] min-[350px]:h-[15px] lg:h-[20px] flex-none"></div>
        </div>

      </div>
    </section>
  );
}

